<template>
  <div class="employee-position-history-tab">
    <!-- Keep search bar visible when there are results OR when user has search/filters (so they can see query and clear) -->
    <div v-if="!shouldShowNoRecordFound || hasFiltersOrSearch" style="margin-bottom: 20px; padding: 0 16px;">
      <SearchBar
        type="button"
        ghostText="Search positions"
        :add="false"
        :label="appliedFilters?.length > 0 ? 'More Filters' : 'Filter'"
        :length="appliedFilters?.length"
        :chipText="appliedFilters"
        @onClick="emitCorrectOnClick"
        @onSearch="handleSearch"
        @removeFilter="removeFilter"
        :valueSearch="searchInput"
      />
    </div>
    <SearchModal
      :dialog="showFilterDialog"
      @back="showFilterDialog = false"
      :statusOptions="statusOptions"
      :filterGroups="filterGroups"
      @checkedItemsChanged="filterAssignments"
      :clearSelection="clearSelection"
      :defaultSelected="appliedFilters"
    />
    <SharedTableSkeleton
      v-if="showSkeletonLoading"
      :row-count="5"
      :column-count="4"
      column-template="2fr 1fr 1fr 80px"
    />
    <NoRecordFound
      v-else-if="shouldShowNoRecordFound"
      :title="''"
      :subtitle="''"
      message="Assignment"
      class="no-record-found"
      :shouldShowAddAction="false"
      :noBorder="true"
      :showActiveCounter="false"
      :showClearAction="hasFiltersOrSearch"
      :clearLabel="clearBtnLabel"
      @onClear="clearSearchFilter"
      :description="hasFiltersOrSearch ? emptyRecordDescription : ''"
      :showSpecificMessage="noRecordMessage"
    />
    <EmployeePositionHistoryTree
      v-else
      ref="positionHistoryTree"
      :filtered-items="filteredItems"
      :top-level="topLevel"
      :remove-border="removeBorder"
      :data-source="filteredItems"
      :data-source-obj="dataSourceObj"
      @treeExpanded="handleTreeExpansionByKey"
      @getEmployeeDetails="handleGetEmployeeDetails"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, getCurrentInstance, onMounted } from 'vue';
import EmployeePositionHistoryTree from '@components/EmployeeAssignments/EmployeePositionHistoryTree.vue';
import SearchBar from '@components/SearchBar/SearchBar.vue';
import SearchModal from '@components/SearchBar/SearchModal.vue';
import NoRecordFound from '@components/NoRecordFound/NoRecordFound.vue';
import SharedTableSkeleton from '@components/SharedTableSkeleton/SharedTableSkeleton.vue';
import { LD_FLAG_IDEA_MDP_560 } from '@components/constants/launchDarklyFlagKeys';
import { getRoleAssignment, getResponsibilityAssignment } from '@/api';

/**
 * Vue 2.7 coexistence shim: Composition API + <script setup> run on the Vue 2.7
 * host without @vue/compat. Route access still goes through getCurrentInstance()
 * because vue-router@3 has no useRoute().
 */
const { proxy } = getCurrentInstance();

const props = defineProps({
  dataSource: {
    type: Array,
    default: () => [],
  },
  topLevel: {
    type: String,
    validator(value) {
      return ['position', 'role', 'responsibility', 'knowledge'].includes(value);
    },
    default: 'position',
  },
  removeBorder: {
    type: Boolean,
    default: false,
  },
  storePersistFilters: {
    type: Boolean,
    default: false,
  },
  storeFiltersKeyPrefix: {
    type: String,
    default: 'employeeAssignments',
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'appliedFilter',
  'searchTextData',
  'treeExpanded',
  'getEmployeeDetails',
  'onFilter',
]);

const allItems = ref([]);
const dataSourceObj = ref([]);
const filteredItems = ref([]);
const searchInput = ref('');
const appliedFilters = ref(['active']);
const hasUserAppliedFilters = ref(false);
const showFilterDialog = ref(false);
const clearSelection = ref('');
const statusOptions = ref(['active', 'pending', 'relieved', 'terminated', 'retired']);
const filterGroups = ref([]);
const positionHistoryTree = ref(null);
/** ID-keyed expanded rows — capture before refresh, restore after applyFilters */
const expandedIds = ref(new Set());

/** MDP-560 / ER-10595 — gate skeleton loading behind the consistent-skeleton feature flag. */
const skeletonLoadingEnabled = ref(false);

onMounted(async () => {
  try {
    skeletonLoadingEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
  } catch (error) {
    skeletonLoadingEnabled.value = false;
  }
});

const showSkeletonLoading = computed(() => props.loading && skeletonLoadingEnabled.value);

const clearState = computed(() => {
  const hasSearch = searchInput.value?.length > 0;
  const hasFilters = hasUserAppliedFilters.value;

  if (hasSearch && hasFilters) {
    return {
      label: 'Clear All',
      title: 'No assignments found',
      description: 'Your search did not match any assignments. Please clear search try again.',
    };
  }
  if (hasSearch) {
    return {
      label: 'Clear Search',
      title: 'No assignments found',
      description: 'Your search did not match any assignments. Please clear search try again.',
    };
  }
  if (hasFilters) {
    return {
      label: 'Clear Filter',
      title: 'No assignments found',
      description: 'Your filters did not match any assignments. Please clear filters try again.',
    };
  }
  return {
    label: 'Clear',
    title: '',
    description: '',
  };
});

const clearBtnLabel = computed(() => clearState.value.label);
const emptyRecordDescription = computed(() => clearState.value.description);

const hasNoAssignmentsEver = computed(() => allItems.value.length === 0);
const filterOrSearchReturnsEmpty = computed(
  () => allItems.value.length > 0 && filteredItems.value.length === 0
);
const hasFiltersOrSearch = computed(
  () => searchInput.value?.length > 0 || hasUserAppliedFilters.value
);
const shouldShowNoRecordFound = computed(() => filteredItems.value.length === 0);

const noRecordMessage = computed(() => {
  if (filterOrSearchReturnsEmpty.value) return 'No assignments found';
  if (hasNoAssignmentsEver.value) return 'This employee was never assigned to a Position';
  return 'No assignments found';
});

const captureExpandedIds = () => {
  const fromTree = positionHistoryTree.value?.getExpandedRowKeys?.() || [];
  if (fromTree.length > 0) {
    expandedIds.value = new Set(fromTree);
  }
  return [...expandedIds.value];
};

const trackExpandedId = (id) => {
  if (id == null) return;
  const next = new Set(expandedIds.value);
  next.add(id);
  expandedIds.value = next;
};

const handleTreeExpansionByKey = ({ assignmentId, rowType, expandedRowData }) => {
  if (expandedRowData?.id != null) {
    trackExpandedId(expandedRowData.id);
  }
  if (expandedRowData) {
    handleTreeExpansion(expandedRowData);
  }
  emit('treeExpanded', { assignmentId, rowType, expandedRowData });
};

const handleTreeExpansion = async (expandedRowData) => {
  const assignmentId = expandedRowData?.assignment_id || expandedRowData?.id;
  const rowType = expandedRowData?.data?.type || 'position';
  const treeNodeId = expandedRowData?.id;

  const accountID = localStorage.getItem('currentAccountID');
  const empId = proxy.$route?.params?.id || proxy.$route?.query?.employeeId;

  if (!empId || !accountID || !expandedRowData) return;

  removeSkeletonLoaders(treeNodeId);

  try {
    if (rowType === 'position') {
      await handlePositionExpansion(accountID, empId, assignmentId, expandedRowData);
    } else if (rowType === 'role') {
      await handleRoleExpansion(accountID, empId, assignmentId, expandedRowData);
    }
  } catch (error) {
    console.error('Error loading assignments:', error);
    removeSkeletonLoaders(treeNodeId);
  }
};

const handlePositionExpansion = async (
  accountID,
  empId,
  positionAssignmentId,
  positionData
) => {
  const treeNodeId = positionData?.id || positionAssignmentId;

  const existingRoles = dataSourceObj.value.filter(
    (item) =>
      item.parentId === treeNodeId &&
      item.data?.type === 'role' &&
      !item.data?.isSkeletonLoader
  );

  if (existingRoles.length > 0) {
    return;
  }

  const roleCount = positionData?.data?.roleCount || 2;
  addSkeletonLoaders(treeNodeId, 'position', roleCount);

  try {
    const response = await getRoleAssignment(accountID, empId, positionAssignmentId);

    removeSkeletonLoaders(treeNodeId);

    if (response.data?.results?.length > 0) {
      const today = new Date();
      const transformedRoles = response.data.results.map((role) =>
        transformRoleData(role, treeNodeId, today)
      );

      dataSourceObj.value = [...dataSourceObj.value, ...transformedRoles];
      allItems.value = [...allItems.value, ...transformedRoles];
      applyFilters();
    }
  } catch (error) {
    removeSkeletonLoaders(treeNodeId);
    throw error;
  }
};

const handleRoleExpansion = async (
  accountID,
  empId,
  roleAssignmentId,
  expandedRowData
) => {
  const existingResponsibilities = dataSourceObj.value.filter(
    (item) =>
      item.parentId === expandedRowData.id &&
      item.data?.type === 'responsibility' &&
      !item.data?.isSkeletonLoader
  );

  if (existingResponsibilities.length > 0) {
    return;
  }

  const responsibilityCount = expandedRowData.data?.responsibilityCount || 3;
  addSkeletonLoaders(expandedRowData.id, 'role', responsibilityCount);

  try {
    const response = await getResponsibilityAssignment(accountID, roleAssignmentId);

    removeSkeletonLoaders(expandedRowData.id);

    if (response.data?.results?.length > 0) {
      const today = new Date();
      const transformedResponsibilities = response.data.results.map((resp) =>
        transformResponsibilityDataFromAPI(
          resp,
          expandedRowData.id,
          roleAssignmentId,
          today
        )
      );

      dataSourceObj.value = [...dataSourceObj.value, ...transformedResponsibilities];
      allItems.value = [...allItems.value, ...transformedResponsibilities];
      applyFilters();
    }
  } catch (error) {
    removeSkeletonLoaders(expandedRowData.id);
    throw error;
  }
};

const transformRoleData = (role, parentId, today) => {
  const isRoleNew =
    new Date(role.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

  let acceptanceStatus = null;
  if (role.status === 'active') {
    acceptanceStatus = isRoleNew ? 'pending' : 'accepted';
  }

  return {
    id: role.id,
    parentId: parentId,
    data: {
      type: role.assignment_type,
      commitmentLevel: role.commitment_level,
      assignmentType: role.assignment_type,
      importanceLevel: role.importance,
      importanceSelector: true,
      totalSteps: 3,
      code: role.role_code,
      name: role.role_name,
      assignedOn: role.assigned_on,
      removedOn: role.removed_on,
      assignedBy: role.assigned_by,
      removedBy: role.removed_by,
      reason: role.reason,
      isNew: isRoleNew,
      isAccepted: !isRoleNew && role.status === 'active',
      acceptanceStatus: acceptanceStatus,
      assignment_id: parentId,
      hasResponsibilities: role.responsibility_assignment_count > 0,
      responsibilityCount: role.responsibility_assignment_count,
    },
    assignment_id: role.id,
    status: role.status || 'active',
    readiness: role.current_readiness,
    hasItems: role.responsibility_assignment_count > 0,
    effectiveData: {},
    assignedData: {},
    removedData: {},
    reasonData: {},
  };
};

const transformResponsibilityDataFromAPI = (resp, parentId, assignmentId, today) => {
  const isRespNew =
    new Date(resp.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

  let badge = null;
  if (resp.badge_level !== null && resp.badge_status !== 'expired') {
    badge = {
      id: resp.badge_id,
      level: resp.badge_level,
      status: resp.badge_issued_method,
      expiration_date: resp.badge_expiration_date,
      issued_method: resp.badge_issued_method,
    };
  }

  let acceptanceStatus = null;
  if (resp.status === 'active') {
    acceptanceStatus = isRespNew ? 'pending' : 'accepted';
  }

  return {
    id: resp.id,
    parentId: parentId,
    data: {
      type: resp.assignment_type,
      commitmentLevel: resp.commitment_level,
      assignmentType: resp.assignment_type,
      importanceLevel: resp.importance,
      importanceSelector: true,
      totalSteps: 3,
      code: resp.responsibility_code,
      name: resp.responsibility_name,
      assignedOn: resp.assigned_on,
      removedOn: resp.removed_on,
      assignedBy: resp.assigned_by,
      removedBy: resp.removed_by,
      reason: resp.reason,
      isNew: isRespNew,
      isAccepted: !isRespNew && resp.status === 'active',
      acceptanceStatus: acceptanceStatus,
      assignment_id: assignmentId,
      badge: badge,
      responsibility_id: resp.responsibility,
    },
    status: resp.status || 'active',
    readiness: resp.current_readiness,
    hasItems: false,
    effectiveData: {},
    assignedData: {},
    removedData: {},
    reasonData: {},
  };
};

const addSkeletonLoaders = (parentId, rowType, count = null) => {
  const existingSkeletons = dataSourceObj.value.filter(
    (item) => item.parentId === parentId && item.data?.isSkeletonLoader
  );

  if (existingSkeletons.length > 0) {
    return;
  }

  const skeletonCount = count || (rowType === 'position' ? 2 : 3);
  const skeletonItems = [];

  for (let i = 0; i < skeletonCount; i++) {
    const skeletonId = `skeleton-${parentId}-${i}`;
    skeletonItems.push({
      id: skeletonId,
      parentId: parentId,
      data: {
        type: 'skeleton',
        assignmentType: rowType === 'position' ? 'role' : 'responsibility',
        skeletonFor: rowType,
        isSkeletonLoader: true,
        commitmentLevel: '',
        importanceLevel: 0,
        importanceSelector: false,
        totalSteps: 3,
        code: '',
        name: '',
        assignedOn: '',
        removedOn: '',
        assignedBy: null,
        removedBy: null,
        reason: '',
        isNew: false,
        isAccepted: false,
        acceptanceStatus: 'pending',
      },
      readiness: 0,
      status: 'loading',
      hasItems: false,
      effectiveData: {},
      assignedData: {},
      removedData: {},
      reasonData: {},
    });
  }

  dataSourceObj.value = [...dataSourceObj.value, ...skeletonItems];
  allItems.value = [...allItems.value, ...skeletonItems];
  applyFilters();
};

const removeSkeletonLoaders = (parentId) => {
  dataSourceObj.value = dataSourceObj.value.filter((item) => {
    const isSkeletonForParent = item.id.toString().startsWith(`skeleton-${parentId}-`);
    const isSkeletonLoader = item.data?.isSkeletonLoader;
    const hasCorrectParent = item.parentId === parentId;

    return !(isSkeletonForParent && isSkeletonLoader && hasCorrectParent);
  });
  allItems.value = allItems.value.filter((item) => {
    const isSkeletonForParent = item.id.toString().startsWith(`skeleton-${parentId}-`);
    const isSkeletonLoader = item.data?.isSkeletonLoader;
    const hasCorrectParent = item.parentId === parentId;

    return !(isSkeletonForParent && isSkeletonLoader && hasCorrectParent);
  });
  applyFilters();
};

const resetExpandedState = () => {
  expandedIds.value = new Set();
  positionHistoryTree.value?.resetExpandedState?.();
};

const reloadExpandedBranches = async (expandedKeys) => {
  for (const key of expandedKeys) {
    const rowData = allItems.value.find((item) => item.id === key);
    if (rowData?.hasItems) {
      await handleTreeExpansion(rowData);
    }
  }
};

const handleGetEmployeeDetails = () => {
  emit('getEmployeeDetails');
};

const handleSearch = (searchText) => {
  searchInput.value = searchText;
  applyFilters();
  emit('searchTextData', searchText);
};

const emitCorrectOnClick = (onClickType) => {
  if (onClickType === 'filter') {
    showFilterDialog.value = true;
    emit('onFilter');
  }
};

const filterAssignments = (filters) => {
  const newArray = filters
    ?.map((item) => {
      if (item.value !== null) {
        if (typeof item === 'string') {
          return item;
        }
        if (
          typeof item === 'object' &&
          Object.prototype.hasOwnProperty.call(item, 'value')
        ) {
          return item.value;
        }
      }
      return null;
    })
    ?.filter(Boolean);
  appliedFilters.value = newArray;
  hasUserAppliedFilters.value = newArray.length > 0;
  showFilterDialog.value = false;
  applyFilters();
  emit('appliedFilter', newArray);
};

const hasActiveTopLevelRecords = () =>
  allItems.value.some((item) => item.parentId === -1 && item.status === 'active');

const normalizeDefaultActiveFilter = () => {
  const onlyDefaultActive =
    appliedFilters.value.length === 1 && appliedFilters.value[0] === 'active';
  if (onlyDefaultActive && !hasActiveTopLevelRecords()) {
    appliedFilters.value = [];
    hasUserAppliedFilters.value = false;
  }
};

/** Always replace the array reference — never mutate in place (Vue 2.7 / Vue 3). */
const removeFilter = (removedFilter) => {
  clearSelection.value = removedFilter;
  appliedFilters.value = appliedFilters.value.filter((f) => f !== removedFilter);
  hasUserAppliedFilters.value = appliedFilters.value.length > 0;
  normalizeDefaultActiveFilter();
  applyFilters();
  emit('appliedFilter', appliedFilters.value);
};

const applyFilters = () => {
  let filtered = [...allItems.value];

  if (appliedFilters.value.length > 0) {
    const matchingTopLevelIds = new Set();
    allItems.value.forEach((item) => {
      if (item.parentId === -1 && appliedFilters.value.includes(item.status)) {
        matchingTopLevelIds.add(item.id);
      }
    });

    const includeDescendants = (parentId) => {
      const children = allItems.value.filter((item) => item.parentId === parentId);
      children.forEach((child) => {
        matchingTopLevelIds.add(child.id);
        includeDescendants(child.id);
      });
    };

    matchingTopLevelIds.forEach((id) => {
      includeDescendants(id);
    });

    filtered = filtered.filter((item) => {
      if (item.parentId === -1) {
        return appliedFilters.value.includes(item.status);
      }
      let currentParentId = item.parentId;
      while (currentParentId !== -1) {
        const parent = allItems.value.find((p) => p.id === currentParentId);
        if (!parent) break;
        if (matchingTopLevelIds.has(parent.id)) {
          return true;
        }
        currentParentId = parent.parentId;
      }
      return false;
    });

    if (
      filtered.length === 0 &&
      !hasUserAppliedFilters.value &&
      appliedFilters.value.length === 1 &&
      appliedFilters.value[0] === 'active'
    ) {
      appliedFilters.value = [];
      hasUserAppliedFilters.value = false;
      filtered = [...allItems.value];
    }
  }

  if (searchInput.value && searchInput.value.trim()) {
    const searchTerm = searchInput.value.trim().toLowerCase();
    const matchingIds = new Set();

    allItems.value.forEach((item) => {
      const name = item.data?.name?.toLowerCase() || '';
      const code = item.data?.code?.toLowerCase() || '';
      if (name.includes(searchTerm) || code.includes(searchTerm)) {
        matchingIds.add(item.id);
        let currentParentId = item.parentId;
        while (currentParentId !== -1) {
          matchingIds.add(currentParentId);
          const parent = allItems.value.find((p) => p.id === currentParentId);
          if (!parent) break;
          currentParentId = parent.parentId;
        }
      }
    });

    const includeDescendants = (parentId) => {
      const children = filtered.filter((item) => item.parentId === parentId);
      children.forEach((child) => {
        matchingIds.add(child.id);
        includeDescendants(child.id);
      });
    };

    matchingIds.forEach((id) => {
      includeDescendants(id);
    });

    filtered = filtered.filter((item) => matchingIds.has(item.id));
  }

  filteredItems.value = filtered;
};

// Watch must be registered after applyFilters / reloadExpandedBranches (const TDZ).
watch(
  () => props.dataSource,
  (newVal) => {
    const expandedKeys = captureExpandedIds();

    allItems.value = newVal || [];
    dataSourceObj.value = newVal || [];

    const hasActiveRecords = allItems.value.some(
      (item) => item.parentId === -1 && item.status === 'active'
    );
    if (
      !hasActiveRecords &&
      !hasUserAppliedFilters.value &&
      appliedFilters.value.length === 1 &&
      appliedFilters.value[0] === 'active'
    ) {
      appliedFilters.value = [];
      hasUserAppliedFilters.value = false;
      filteredItems.value = allItems.value;
      emit('appliedFilter', []);
    } else {
      applyFilters();
    }

    if (expandedKeys.length > 0) {
      nextTick(() => reloadExpandedBranches(expandedKeys));
    }
  },
  { deep: true, immediate: true }
);

const clearSearchFilter = () => {
  const hasSearch = searchInput.value?.length > 0;
  const hasFilters = hasUserAppliedFilters.value;

  if (hasSearch && hasFilters) {
    appliedFilters.value = [];
    hasUserAppliedFilters.value = false;
    searchInput.value = '';
    clearSelection.value = '';
    emit('appliedFilter', []);
    emit('searchTextData', '');
    applyFilters();
    resetExpandedState();
  } else if (hasSearch) {
    searchInput.value = '';
    emit('searchTextData', '');
    applyFilters();
  } else if (hasFilters) {
    appliedFilters.value = [];
    hasUserAppliedFilters.value = false;
    clearSelection.value = '';
    emit('appliedFilter', []);
    applyFilters();
    resetExpandedState();
  }
};

defineExpose({
  allItems,
  dataSourceObj,
  filteredItems,
  searchInput,
  appliedFilters,
  hasUserAppliedFilters,
  showFilterDialog,
  clearSelection,
  expandedIds,
  skeletonLoadingEnabled,
  showSkeletonLoading,
  noRecordMessage,
  hasNoAssignmentsEver,
  filterOrSearchReturnsEmpty,
  hasFiltersOrSearch,
  shouldShowNoRecordFound,
  handleTreeExpansion,
  handleTreeExpansionByKey,
  reloadExpandedBranches,
  resetExpandedState,
  removeFilter,
  filterAssignments,
  applyFilters,
  clearSearchFilter,
  handleSearch,
  normalizeDefaultActiveFilter,
  positionHistoryTree,
});
</script>

<style scoped>
.employee-position-history-tab {
  width: 100%;
}
</style>
